import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, query, getDocs, doc, setDoc, where, limit, writeBatch, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { getTeamMemberIds } from '../utils/teamUtils';
import { checkCustomerAgent, updateCustomerAgent } from '../services/customerService';
import { ConfirmModal } from '../components/common/ConfirmModal';

const DemoContext = createContext();

export function DemoProvider({ children }) {
  const [demoBookings, setDemoBookings] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hostsLoading, setHostsLoading] = useState(false);
  const [showHostConflict, setShowHostConflict] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [conflictResolutionResult, setConflictResolutionResult] = useState(null);
  
  const { currentAgent } = useAuth();
  const pendingDemoRef = useRef(null);

  // Load hosts for the current agent
  const loadHosts = useCallback(async () => {
    if (!currentAgent?.agent_id) return;
    
    try {
      setHostsLoading(true);
      const hostsRef = collection(db, 'plsss_hosts');
      const q = query(hostsRef, where('agentId', '==', currentAgent.agent_id));
      
      const querySnapshot = await getDocs(q);
      const hostsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setHosts(hostsList);
    } catch (error) {
      console.error('Error loading hosts:', error);
      setHosts([]);
    } finally {
      setHostsLoading(false);
    }
  }, [currentAgent?.agent_id]);

  // Check if a host with the given phone number exists
  const checkExistingHost = useCallback(async (phoneNumber) => {
    console.log('checkExistingHost called with phone:', phoneNumber);
    if (!phoneNumber) {
      console.error('No phone number provided to checkExistingHost');
      return { exists: false, error: 'No phone number provided' };
    }
    
    try {
      const hostsRef = collection(db, 'plsss_hosts');
      const q = query(hostsRef, where('phone', '==', phoneNumber), limit(1));
      console.log('Executing query for phone:', phoneNumber);
      
      const querySnapshot = await getDocs(q);
      console.log('Query completed, found docs:', querySnapshot.docs.length);
      
      if (querySnapshot.empty) {
        console.log('No existing host found for phone:', phoneNumber);
        return { exists: false };
      }
      
      const hostDoc = querySnapshot.docs[0];
      const hostData = hostDoc.data();
      const isCurrentAgent = hostData.agentId === currentAgent?.agent_id;
      
      console.log('Found existing host:', {
        hostId: hostDoc.id,
        agentId: hostData.agentId,
        currentAgentId: currentAgent?.agent_id,
        isCurrentAgent,
        hostData: JSON.stringify(hostData, null, 2)
      });
      
      return {
        exists: true,
        id: hostDoc.id,
        data: hostData,
        isCurrentAgent,
        agentId: hostData.agentId,
        agentName: hostData.agentName,
        agentEmail: hostData.agentEmail
      };
    } catch (error) {
      console.error('Error checking host:', error);
      return { exists: false, error: error.message };
    }
  }, [currentAgent?.agent_id]);

  // Create a new demo booking
  const createDemoBooking = useCallback(async (demoData, hasHostConflict = false) => {
    if (!currentAgent?.agent_id) {
      console.error('No agent ID available');
      return { success: false, error: 'No agent ID available' };
    }
    
    // Reset conflict data if this is not a conflict resolution
    if (!hasHostConflict) {
      setShowHostConflict(false);
      setConflictData(null);
    }
    
    try {
      setLoading(true);
      
      // Validate required fields
      if (!demoData.customerMobile) {
        throw new Error('Customer mobile number is required');
      }
      
      // Check customer agent association
      console.log('Checking customer agent association for:', demoData.customerMobile);
      const customerCheck = await checkCustomerAgent(
        demoData.customerMobile,
        currentAgent.agent_id
      );
      
      if (!customerCheck.available) {
        return { 
          success: false, 
          error: 'CUSTOMER_CLAIMED',
          existingAgentId: customerCheck.existingAgentId,
          message: 'This customer is already associated with another agent.'
        };
      }

      // Generate demo ID
      const now = new Date();
      const demoId = `W${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}-${currentAgent.agent_id}`;
      
      // Prepare demo booking data
      const role = currentAgent?.role || 'user';
      
      // For hosts and booking agents, use associated agent code instead of their own agent ID
      let agentCodeForPayload = currentAgent.agent_id;
      if ((role === 'host' || role === 'booking') && demoData.associatedAgentCode) {
        agentCodeForPayload = demoData.associatedAgentCode;
        console.log('Using associated agent code for', role, ':', agentCodeForPayload);
      } else if (role === 'host' || role === 'booking') {
        console.warn(role + ' booking without associated agent code - using agent ID as fallback');
      }
      
      const demoBooking = {
        payload: {
          agentCode: agentCodeForPayload,
          customer: {
            country_iso: demoData.country_iso || 'BHR',
            mobile: demoData.customerMobile,
            name: demoData.customerName,
            phone_prefix: demoData.phonePrefix
          },
          dateTime: {
            demoDate: demoData.demoDate,
            timeSlot: demoData.timeSlot,
            timestamp: new Date().toISOString()
          },
          demoId,
          trainingType: demoData.trainingType,
          trnCode: demoData.trnCode || (currentAgent?.apps?.agents?.teamLeader || currentAgent.agent_id),
          location: demoData.demoLocation,
          hostedDemo: {
            isHosted: role === 'host' ? 'YES' : (demoData.isHosted === 'YES' ? 'YES' : 'NO'),
            hostDetails: { isHosted: 'NO' }, // Default structure with hosted: "NO"
            bkpCode: demoData.bkpCode || null, // At hostedDemo level, not inside hostDetails
            // Add hosting gift code and name if demo is hosted and gift is selected
            ...((role === 'host' || demoData.isHosted === 'YES') && demoData.hostingGiftCode && {
              giftCode: demoData.hostingGiftCode,
              giftName: demoData.hostingGiftName
            })
          },
          // Add referrer field if user is a referrer
          ...(role === 'referrer' && { referrer: currentAgent.agent_id })
        }
      };

      // Handle host details if needed
      console.log('Checking host details:', {
        role,
        demoLocation: demoData.demoLocation,
        isHosted: demoData.isHosted,
        hostPhone: demoData.hostPhone,
        hasHostConflict,
        conflictResolved: demoData.conflictResolved,
        associatedAgentCode: demoData.associatedAgentCode
      });

      // For hosts and booking agents, always populate host details from their own profile
      if (role === 'host' || role === 'booking') {
        const hostDetails = {
          name: currentAgent.name,
          phone: currentAgent.phoneno || currentAgent.phone || currentAgent.mobile || 'unknown', // Use phoneno first, then phone, then mobile
          hostId: currentAgent.agent_id
        };
        
        // Add BkpCode for booking agents at hostedDemo level (not inside hostDetails)
        if (role === 'booking') {
          demoBooking.payload.hostedDemo.bkpCode = currentAgent.agentCode || currentAgent.agent_id;
        }
        
        demoBooking.payload.hostedDemo.hostDetails = hostDetails;
        console.log('Host details populated for role:', role, hostDetails);
        console.log('Current agent phone fields:', {
          phoneno: currentAgent.phoneno,
          phone: currentAgent.phone,
          mobile: currentAgent.mobile,
          agent_id: currentAgent.agent_id
        });
      } else if (demoData.isHosted === 'YES' && demoData.hostPhone) {
        const hostDetails = {
          name: demoData.hostName || '',
          phone: demoData.hostPhone
        };

        const hostCheck = await checkExistingHost(demoData.hostPhone);
        console.log('Host check result:', {
          exists: hostCheck.exists,
          data: hostCheck.data,
          isCurrentAgent: hostCheck.isCurrentAgent,
          conflictResolved: demoData.conflictResolved
        });
        
        if (hostCheck.exists && hostCheck.data) {
          const isOurHost = hostCheck.isCurrentAgent || demoData.conflictResolved;
          const hostData = hostCheck.data;
          
          // Log the conflict if it's not our host and not already resolved
          if (!isOurHost && !demoData.conflictResolved) {
            console.log('Detected new conflict for host:', hostCheck.id);
            
            try {
              const batch = writeBatch(db);
              const hostRef = doc(db, 'plsss_hosts', hostCheck.id);
              
              // Create a reference to the conflict_agents subcollection
              const conflictAgentsRef = collection(db, 'plsss_hosts', hostCheck.id, 'conflict_agents');
              const newConflictRef = doc(conflictAgentsRef);
              
              // Prepare conflict data with all relevant details
              const conflictData = {
                id: newConflictRef.id,
                timestamp: new Date().toISOString(),
                demoBookingId: demoId || 'unknown',
                currentAgent: {
                  id: currentAgent?.agent_id || 'unknown_agent',
                  name: currentAgent?.name || 'Unknown Agent',
                  email: currentAgent?.email || '',
                  timestamp: new Date().toISOString()
                },
                originalAgent: {
                  id: hostData?.agentId || 'unknown_agent',
                  name: hostData?.agentName || 'Unknown Agent',
                  email: hostData?.agentEmail || ''
                },
                customer: {
                  mobile: demoData.customerMobile || 'unknown',
                  name: demoData.customerName || 'Unknown Customer',
                  country: demoData.country_iso || 'BHR'
                },
                demoDetails: {
                  date: demoData.demoDate || null,
                  timeSlot: demoData.timeSlot || null,
                  location: demoData.demoLocation || null,
                  trainingType: demoData.trainingType || null
                },
                status: 'pending',
                resolved: false,
                resolvedAt: null,
                resolvedBy: null,
                metadata: {
                  isHosted: demoData.isHosted === 'YES',
                  hostPhone: demoData.hostPhone || null,
                  hostName: demoData.hostName || null,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }
              };
              
              // Update the host document
              const newConflictCount = (hostData.conflictCount || 0) + 1;
              batch.update(hostRef, {
                hasConflict: true,
                lastConflictAt: new Date().toISOString(),
                conflictCount: newConflictCount,
                updatedAt: new Date().toISOString(),
                lastConflict: {
                  timestamp: new Date().toISOString(),
                  bookingId: demoId,
                  customerMobile: demoData.customerMobile,
                  currentAgentId: currentAgent?.agent_id,
                  status: 'pending',
                  conflictId: newConflictRef.id
                }
              });

              // Add the conflict to the subcollection
              batch.set(newConflictRef, conflictData);

              // Commit the batch
              await batch.commit();
              
              console.log('Conflict logged successfully in subcollection:', {
                hostId: hostCheck.id,
                conflictId: newConflictRef.id,
                conflictCount: newConflictCount,
                path: `plsss_hosts/${hostCheck.id}/conflict_agents/${newConflictRef.id}`
              });
              
              // Return success with conflict resolution data
              return {
                success: true,
                demoId,
                conflictResolved: true,
                hostName: demoData.hostName || hostData?.name || 'Unknown Host',
                customerName: demoData.customerName || 'Unknown Customer',
                shouldCloseForm: true,
                message: 'Demo booked with conflict resolution'
              };
              
            } catch (error) {
              console.error('Error logging conflict in subcollection:', error);
              throw new Error('Failed to log conflict: ' + error.message);
            }
          }
          
          if (isOurHost) {
            // Use the existing host data
            hostDetails.id = hostCheck.id;
            hostDetails.name = demoData.hostName || hostData.name || '';
            hostDetails.phone = demoData.hostPhone || hostData.phone;
            
            // If it's our host and the name changed, update it
            if (hostCheck.isCurrentAgent && demoData.hostName && demoData.hostName !== hostData.name) {
              const hostRef = doc(db, 'plsss_hosts', hostCheck.id);
              await setDoc(hostRef, {
                ...hostData,
                name: demoData.hostName,
                updatedAt: new Date().toISOString()
              }, { merge: true });
            }
            
            // If this was a conflict resolution, mark it
            if (!hostCheck.isCurrentAgent && hostData.agentId) {
              hostDetails.duplicate_agent = hostData.agentId;
            }
          } else if (!hasHostConflict) {
            // Show conflict modal for the first time
            console.log('Showing host conflict dialog');
            pendingDemoRef.current = demoData;
            setConflictData({
              hostPhone: demoData.hostPhone,
              existingAgentId: hostData.agentId,
              hostName: hostData.name
            });
            setShowHostConflict(true);
            return { 
              waitingForConfirmation: true,
              message: `This host is already managed by another agent (${hostData.agentId}). Do you want to proceed?`
            };
          } else {
            // If we have a conflict and user chose to proceed, but we're not the owner
            console.log('Processing booking with conflicting host after user confirmation', {
              hostId: hostCheck.id,
              existingAgent: hostData.agentId,
              currentAgent: currentAgent.agent_id
            });
            
            // Update the host's record to track this conflict
            const hostRef = doc(db, 'plsss_hosts', hostCheck.id);
            await setDoc(hostRef, {
              ...hostData,
              conflict_resolutions: [
                ...(hostData.conflict_resolutions || []),
                {
                  resolvedBy: currentAgent.agent_id,
                  resolvedAt: new Date().toISOString(),
                  bookingId: demoId,
                  customerMobile: demoData.customerMobile
                }
              ],
              updatedAt: new Date().toISOString()
            }, { merge: true });
            
            // Use the existing host data but mark it as a duplicate
            hostDetails.id = hostCheck.id;
            hostDetails.name = hostData.name || demoData.hostName || '';
            hostDetails.phone = hostData.phone || demoData.hostPhone;
            hostDetails.duplicate_agent = hostData.agentId;
            
            // Add conflict resolution metadata to the booking
            demoBooking.payload.conflictResolved = true;
            demoBooking.payload.conflictResolvedAt = new Date().toISOString();
            demoBooking.payload.originalAgentId = hostData.agentId;
            demoBooking.payload.conflictResolution = {
              resolvedBy: currentAgent.agent_id,
              resolvedAt: new Date().toISOString(),
              originalAgentId: hostData.agentId
            };
            
            console.log('Proceeding with duplicate host details:', hostDetails);
          }
        } else {
          // Create new host
          const hostPhoneId = demoData.hostPhone.replace(/^\+/, '');
          const hostRef = doc(db, 'plsss_hosts', hostPhoneId);
          await setDoc(hostRef, {
            active: true,
            agentId: currentAgent.agent_id,
            country_iso: demoData.country_iso || 'BHR',
            multi_agent: true,
            name: demoData.hostName,
            phone: demoData.hostPhone,
            phone_prefix: demoData.phonePrefix || '+973',
            createdAt: new Date().toISOString()
          });
          await loadHosts();
          
          // Set host details for the booking
          hostDetails.id = hostPhoneId;
          hostDetails.name = demoData.hostName;
          hostDetails.phone = demoData.hostPhone;
        }
        
        demoBooking.payload.hostedDemo.hostDetails = hostDetails;
      }

      // Mutual exclusivity: if bkpCode present (even empty string), set hostDetails to null
      if (demoBooking.payload.hostedDemo.bkpCode !== null && demoBooking.payload.hostedDemo.bkpCode !== undefined) {
        demoBooking.payload.hostedDemo.hostDetails = null;
        // Gift is optional when bkpCode present (commission-based per Note 5)
        if (!demoData.hostingGiftCode) {
          delete demoBooking.payload.hostedDemo.giftCode;
          delete demoBooking.payload.hostedDemo.giftName;
        }
      } else if (demoBooking.payload.hostedDemo.hostDetails &&
                 (demoBooking.payload.hostedDemo.hostDetails.name ||
                  demoBooking.payload.hostedDemo.hostDetails.phone)) {
        // If hostDetails has actual data, set bkpCode to null
        demoBooking.payload.hostedDemo.bkpCode = null;
        // Gift is mandatory when hostDetails present (per Note 5)
        if (!demoData.hostingGiftCode) {
          throw new Error('Hosting gift is required when host details are specified');
        }
      }
      // If neither bkpCode nor hostDetails has data, both remain as defaults for non-hosted demo

      // Debug: Log complete payload before saving
      console.group('=== DEMO BOOKING PAYLOAD ===');
      console.log('Demo ID:', demoId);
      console.log('Agent ID:', currentAgent.agent_id);
      console.log('Customer Mobile:', demoData.customerMobile);
      console.log('Host Details:', demoBooking.payload.hostedDemo?.hostDetails);
      console.log('Complete Payload:', JSON.parse(JSON.stringify(demoBooking)));
      console.groupEnd();
      
      // Save demo booking
      try {
        const docRef = doc(db, 'demo_bookings', demoId);
        await setDoc(docRef, demoBooking);
        console.log('Demo booking saved successfully');
        
        // Update customer-agent mapping
        try {
          await updateCustomerAgent(
            demoData.customerMobile,
            currentAgent.agent_id,
            demoId
          );
          console.log('Customer-agent mapping updated');
        } catch (updateError) {
          console.error('Error updating customer-agent mapping:', updateError);
          // Continue even if this fails
        }
        
        // Update local state
        setDemoBookings(prev => [{ id: demoId, ...demoBooking }, ...prev]);
        return { 
          success: true, 
          demoId, 
          demo: demoBooking,
          hostName: demoData.hostName,
          customerName: demoData.customerName,
          customer: {
            name: demoData.customerName,
            mobile: demoData.customerMobile,
            country_iso: demoData.country_iso || 'BHR'
          },
          conflictResolved: demoData.conflictResolved || false,
          shouldCloseForm: true
        };
      } catch (saveError) {
        console.error('Failed to save demo booking:', saveError);
        throw new Error(`Failed to save booking: ${saveError.message}`);
      }
      
    } catch (error) {
      console.error('Error creating booking:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to create booking' 
      };
    } finally {
      setLoading(false);
    }
  }, [currentAgent?.agent_id, loadHosts, checkExistingHost]);

  // Handle host conflict confirmation
  const handleHostConflictConfirm = useCallback(async () => {
    if (!pendingDemoRef.current) {
      console.error('No pending demo data in handleHostConflictConfirm');
      return { 
        success: false,
        error: 'No pending demo data',
        shouldCloseForm: false
      };
    }
    
    const demoData = { ...pendingDemoRef.current };
    
    try {
      console.log('1. Starting conflict resolution for:', demoData.hostPhone);
      
      const hostCheck = await checkExistingHost(demoData.hostPhone);
      
      if (hostCheck.exists && hostCheck.data) {
        demoData.hostId = hostCheck.id;
        demoData.hostName = hostCheck.data.name || demoData.hostName || '';
        demoData.conflictResolved = true;
      }
      
      console.log('3. Calling createDemoBooking with conflict resolution data');
      const result = await createDemoBooking(demoData, true);
      
      if (result.success) {
        console.log('5. Conflict resolution successful, setting result');
        
        // Set the conflict resolution result for the form component
        setConflictResolutionResult({
          success: true,
          demoId: result.demoId,
          conflictResolved: true,
          hostName: demoData.hostName,
          customerName: demoData.customerName,
          shouldCloseForm: true
        });
        
        // Clear all state
        pendingDemoRef.current = null;
        setShowHostConflict(false);
        setConflictData(null);
        
        return result;
      }
      
      return result;
      
    } catch (error) {
      console.error('6. Error resolving host conflict:', error);
      return { 
        success: false, 
        error: 'Failed to resolve host conflict. Please try again.',
        shouldCloseForm: false
      };
    }
  }, [checkExistingHost, createDemoBooking]);

  const handleHostConflictCancel = useCallback(() => {
    // Clear all conflict-related state
    pendingDemoRef.current = null;
    setShowHostConflict(false);
    setConflictData(null);
    setConflictResolutionResult(null);
    
    return { success: false, cancelled: true };
  }, []);

  // Clear conflict resolution result
  const clearConflictResolution = useCallback(() => {
    setConflictResolutionResult(null);
  }, []);

  // Map phone prefix to country ISO code
  const getCountryIsoFromPrefix = (phonePrefix) => {
    const prefixMap = {
      '+973': 'BHR', // Bahrain
      '+966': 'SAU', // Saudi Arabia
      '+971': 'ARE', // United Arab Emirates
      '+974': 'QAT', // Qatar
      '+965': 'KWT', // Kuwait
      '+968': 'OMN', // Oman
      '+63': 'PHL',  // Philippines
      'OTHER': 'ZZZ' // Other countries
    };
    return prefixMap[phonePrefix] || 'ZZZ'; // Default to ZZZ for unknown/other countries
  };

  // Book demo function
  const bookDemo = useCallback(async (demoData) => {
    try {
      console.log('Starting bookDemo with data:', demoData);
      
      // Create a copy to avoid modifying the original demoData
      const bookingData = { ...demoData };
      
      // Map phone prefix to country_iso if not already provided
      if (!bookingData.country_iso && bookingData.phonePrefix) {
        bookingData.country_iso = getCountryIsoFromPrefix(bookingData.phonePrefix);
      }
      
      // Store the demo data in the ref for potential conflict resolution
      pendingDemoRef.current = demoData;
      
      // Check for existing host conflicts
      if (bookingData.hostPhone) {
        const hostCheck = await checkExistingHost(bookingData.hostPhone);
        if (hostCheck.exists && !hostCheck.isCurrentAgent && !bookingData.conflictResolved) {
          console.log('Host conflict detected, showing conflict dialog');
          setConflictData({
            existingHost: hostCheck.data,
            newHost: {
              name: bookingData.hostName,
              phone: bookingData.hostPhone
            },
            customerMobile: bookingData.customerMobile
          });
          return { 
            success: false, 
            error: 'HOST_CONFLICT',
            hasHostConflict: true,
            conflictData: hostCheck.data
          };
        }
      }
      
      // If no conflict or already resolved, proceed with booking
      const result = await createDemoBooking(bookingData);
      console.log('Booking result:', result);
      
      // If booking was successful, clear any pending state
      if (result.success) {
        console.log('Booking successful, clearing state');
        pendingDemoRef.current = null;
        setShowHostConflict(false);
        setConflictData(null);
        
        // Return success with form closure flag
        return {
          ...result,
          shouldCloseForm: true,
          message: result.message || 'Demo booked successfully'
        };
      }
      
      return result;
      
    } catch (error) {
      console.error('Error in bookDemo:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to book demo',
        shouldCloseForm: false
      };
    }
  }, [createDemoBooking, checkExistingHost]);

  // Load demo bookings
  const loadDemoBookings = useCallback(async () => {
    if (!currentAgent?.agent_id) return;
    
    try {
      setLoading(true);
      const demosRef = collection(db, 'demo_bookings');
      const agentId = currentAgent?.agent_id;
      
      // Always filter by the agent's own ID, regardless of role
      const q = query(
        demosRef, 
        where('payload.agentCode', '==', agentId)
      );
      
      const querySnapshot = await getDocs(q);
      const demos = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort demos by descending demo date
      demos.sort((a, b) => {
        const dateA = new Date(a.payload?.dateTime?.demoDate || '1970-01-01');
        const dateB = new Date(b.payload?.dateTime?.demoDate || '1970-01-01');
        return dateB.getTime() - dateA.getTime();
      });
      
      setDemoBookings(demos);
    } catch (error) {
      console.error('Error loading bookings:', error);
      setDemoBookings([]);
    } finally {
      setLoading(false);
    }
  }, [currentAgent?.agent_id]);

  // Get pending demos
  const getPendingDemos = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return demoBookings.filter(demo => {
      const demoDate = new Date(demo.payload.dateTime.demoDate);
      return demoDate >= today;
    });
  }, [demoBookings]);

  // Load initial data
  useEffect(() => {
    if (currentAgent?.agent_id) {
      loadDemoBookings();
      loadHosts();
    }
  }, [currentAgent?.agent_id, loadDemoBookings, loadHosts]);

  // Context value
  const contextValue = useMemo(() => ({
    demoBookings,
    hosts,
    loading,
    hostsLoading,
    bookDemo,
    loadDemoBookings,
    getPendingDemos,
    conflictResolutionResult,
    clearConflictResolution
  }), [
    demoBookings, 
    hosts, 
    loading, 
    hostsLoading, 
    bookDemo, 
    loadDemoBookings, 
    getPendingDemos,
    conflictResolutionResult,
    clearConflictResolution
  ]);

  return (
    <>
      <DemoContext.Provider value={contextValue}>
        {children}
      </DemoContext.Provider>
      
      <ConfirmModal
        isOpen={showHostConflict}
        onConfirm={handleHostConflictConfirm}
        onCancel={handleHostConflictCancel}
        title="Host Already Exists"
        message={
          conflictData 
            ? `The phone number ${conflictData.hostPhone} is already associated with another agent (${conflictData.agentId}).\n\nWould you like to proceed with the demo booking without updating the host details?`
            : ''
        }
        confirmText="Yes, Continue"
        cancelText="Cancel Booking"
      />
    </>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
}
